import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, AlertCircle } from 'lucide-react';

const TwoFactorVerificationPage = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Récupérer le temp_token depuis l'état de navigation
  const tempToken = location.state?.tempToken;
  const userId = location.state?.userId;

  useEffect(() => {
    // Si pas de token temporaire, rediriger vers login
    if (!tempToken) {
      navigate('/login');
    }
  }, [tempToken, navigate]);

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    // Valider le format : 6 chiffres (TOTP) OU 9 caractères format XXXX-XXXX (backup)
    const isValidTOTP = /^\d{6}$/.test(code);
    const isValidBackup = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(code);
    
    if (!isValidTOTP && !isValidBackup) {
      setError('Code invalide. Entrez 6 chiffres (TOTP) ou un code de backup (XXXX-XXXX)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-2fa-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          temp_token: tempToken,
          code: code
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Code invalide');
      }

      // Stocker le token et les infos utilisateur
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      try { localStorage.setItem('pitboard_last_activity', String(Date.now())); } catch { /* ignore */ }

      // Rediriger selon le rôle
      if (data.user.role === 'admin' || data.user.role === 'super_admin') {
        navigate('/admin');
      } else if (data.user.role === 'partner') {
        navigate('/partner');
      } else {
        navigate('/client');
      }

      // Recharger pour mettre à jour le contexte d'authentification
      window.location.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    // Accepter les chiffres pour TOTP (6 chiffres) OU format backup XXXX-XXXX (9 caractères avec tiret)
    let value = e.target.value.toUpperCase();
    
    // Si c'est un code backup (contient des lettres ou tiret), accepter format XXXX-XXXX
    if (value.match(/[A-Z-]/)) {
      // Format backup : lettres, chiffres et tiret uniquement, max 9 caractères
      value = value.replace(/[^A-Z0-9-]/g, '').slice(0, 9);
      
      // Ajouter automatiquement le tiret après 4 caractères si pas déjà présent
      if (value.length === 4 && !value.includes('-')) {
        value = value + '-';
      }
    } else {
      // Format TOTP : 6 chiffres uniquement
      value = value.replace(/\D/g, '').slice(0, 6);
    }
    
    setCode(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Shield className="text-blue-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Authentification à 2 Facteurs
          </h1>
          <p className="text-slate-600 text-sm">
            Entrez le code à 6 chiffres de votre application d'authentification ou un code de backup
          </p>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleVerifyCode} className="space-y-6">
          {/* Champ code */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Code de vérification
            </label>
            <input
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder="000000 ou XXXX-XXXX"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg text-center text-2xl font-mono tracking-wider focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none uppercase"
              maxLength={9}
              autoFocus
            />
            <p className="mt-2 text-xs text-slate-500">
              Code TOTP (6 chiffres) ou code de secours (XXXX-XXXX)
            </p>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <AlertCircle className="text-red-600 mt-0.5 mr-2 flex-shrink-0" size={20} />
              <div className="text-sm text-red-800">
                {error}
              </div>
            </div>
          )}

          {/* Bouton de soumission */}
          <button
            type="submit"
            disabled={loading || (code.length !== 6 && code.length !== 9)}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Vérification...
              </div>
            ) : (
              'Vérifier le Code'
            )}
          </button>
        </form>

        {/* Aide */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <p className="text-xs text-slate-500 text-center">
            Vous n'avez plus accès à votre application d'authentification ?
            <br />
            <button
              onClick={() => navigate('/login')}
              className="text-blue-600 hover:text-blue-700 font-medium mt-1 inline-block"
            >
              Retour à la connexion
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorVerificationPage;
